/**
 * Phase 3b — Per-contact edit modal.
 *
 * Edits one contact row's name / email / role / company. Used by
 * ContactRowEditor when the operator clicks the per-row "edit"
 * affordance. Submits to the parent via `onSave(updatedDraft)`;
 * the parent splices the result back into the contacts list and
 * fires the per-field edit mutation.
 *
 * Name OR email is required (matches the backend validation rule
 * in deal_card_builder + the field-edit endpoint). The modal
 * disables Save when neither is present.
 */
import { useState } from 'react';

import styles from './DealsPage.module.css';

export interface ContactDraft {
  name?: string;
  email?: string | null;
  role?: string | null;
  company?: string | null;
}

interface Props {
  draft: ContactDraft;
  onCancel: () => void;
  onSave: (next: ContactDraft) => void;
}

export function ContactEditModal({ draft, onCancel, onSave }: Props) {
  const [name, setName] = useState(draft.name || '');
  const [email, setEmail] = useState(draft.email || '');
  const [role, setRole] = useState(draft.role || '');
  const [company, setCompany] = useState(draft.company || '');

  const canSave = (name.trim() || email.trim()).length > 0;

  return (
    <div className={styles.modalBackdrop} onClick={onCancel}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={styles.modalTitle}>Edit contact</h3>
        <div className={styles.modalSubtitle}>
          name OR email is required
        </div>

        <label className={styles.noteLabel}>name</label>
        <input
          type="text"
          className={styles.ciEditor}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        <label className={styles.noteLabel}>email</label>
        <input
          type="email"
          className={styles.ciEditor}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label className={styles.noteLabel}>role</label>
        <input
          type="text"
          className={styles.ciEditor}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder={'e.g. "Managing Director, M&A"'}
        />

        <label className={styles.noteLabel}>company</label>
        <input
          type="text"
          className={styles.ciEditor}
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder={'(blank = inferred from email domain)'}
        />

        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.modalCancel}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.modalConfirm}
            disabled={!canSave}
            onClick={() => onSave({
              name: name.trim() || undefined,
              email: email.trim() || null,
              role: role.trim() || null,
              company: company.trim() || null,
            })}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
